import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Shield, RefreshCw, Search, UserCheck, UserX,
  Eye, EyeOff, Clock, MapPin, Layers, Lock, Mail, ChevronRight
} from 'lucide-react';

const API = API_URL;

const ROLE_CONFIG = {
  platform_superadmin: {
    label: { fr: 'Super Admin', en: 'Super Admin' },
    cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    desc: { fr: 'Accès complet plateforme', en: 'Full platform access' }
  },
  tenant_superuser: {
    label: { fr: 'Superutilisateur', en: 'Superuser' },
    cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
    desc: { fr: 'Accès complet tenant', en: 'Full tenant access' }
  },
  tenant_hr: {
    label: { fr: 'RH / Paie', en: 'HR / Payroll' },
    cls: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-400 border-pink-200 dark:border-pink-800',
    desc: { fr: 'Gestion RH et paie', en: 'HR and payroll management' }
  },
  tenant_admin: {
    label: { fr: 'Administrateur', en: 'Administrator' },
    cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    desc: { fr: 'Gestion opérationnelle', en: 'Operations management' }
  },
  admin: {
    label: { fr: 'Admin (Legacy)', en: 'Admin (Legacy)' },
    cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    desc: { fr: 'Rôle hérité', en: 'Legacy role' }
  },
  tenant_supervisor: {
    label: { fr: 'Superviseur', en: 'Supervisor' },
    cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
    desc: { fr: 'Présence live', en: 'Live presence' }
  },
  employee: {
    label: { fr: 'Employé', en: 'Employee' },
    cls: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600',
    desc: { fr: 'Kiosque uniquement', en: 'Kiosk only' }
  }
};

export default function UsersPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId } = useTenant();
  const fr = language === 'fr';

  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterActive, setFilterActive] = useState('active');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);

  // Detail panel
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '', password: '', firstName: '', lastName: '', role: 'tenant_admin',
    managedSiteIds: [], managedZoneIds: [], shiftId: '', createAsEmployee: true, managerId: ''
  });

  const canManageUsers = user?.permissions?.can_manage_users || user?.role === 'platform_superadmin' || user?.role === 'tenant_superuser';

  /* ========== FETCH ========== */
  const fetchData = useCallback(async () => {
    const tenantId = effectiveTenantId || user?.tenantId;
    if (!tenantId && user?.role !== 'platform_superadmin') return;
    try {
      setLoading(true);
      const params = user?.role === 'platform_superadmin' && !tenantId ? '' : `?tenantId=${tenantId}`;
      const [usersRes, sitesRes, zonesRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/users${params}`),
        tenantId ? axios.get(`${API}/sites?tenantId=${tenantId}`) : Promise.resolve({ data: [] }),
        tenantId ? axios.get(`${API}/zones?tenantId=${tenantId}`) : Promise.resolve({ data: [] }),
        tenantId ? axios.get(`${API}/shifts?tenantId=${tenantId}`) : Promise.resolve({ data: [] }),
      ]);
      setUsers(usersRes.data);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      toast.error(fr ? 'Erreur de chargement' : 'Loading error');
    } finally { setLoading(false); }
  }, [effectiveTenantId, user, fr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ========== FILTERED ========== */
  const filteredUsers = useMemo(() => {
    let result = [...users];
    if (filterActive === 'active') result = result.filter(u => u.active !== false);
    else if (filterActive === 'inactive') result = result.filter(u => u.active === false);
    if (filterRole !== 'all') result = result.filter(u => u.role === filterRole);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u => u.firstName?.toLowerCase().includes(q) || u.lastName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    }
    return result;
  }, [users, searchQuery, filterRole, filterActive]);

  /* ========== KPIs ========== */
  const kpis = useMemo(() => {
    const active = users.filter(u => u.active !== false);
    const inactive = users.filter(u => u.active === false);
    const admins = active.filter(u => ['tenant_superuser', 'tenant_admin', 'tenant_hr', 'admin'].includes(u.role));
    const supervisors = active.filter(u => u.role === 'tenant_supervisor');
    return { total: users.length, active: active.length, inactive: inactive.length, admins: admins.length, supervisors: supervisors.length };
  }, [users]);

  /* ========== HELPERS ========== */
  const getRoleBadge = (role) => {
    const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.employee;
    return <Badge className={`${cfg.cls} border text-[10px]`}>{cfg.label[language] || role}</Badge>;
  };

  const getZoneName = (id) => zones.find(z => z.id === id)?.name;
  const getShiftName = (id) => shifts.find(s => s.id === id)?.name;
  const getSiteName = (id) => sites.find(s => s.id === id)?.name;

  const getAllowedRoles = () => {
    if (user?.role === 'platform_superadmin') return Object.keys(ROLE_CONFIG);
    return ['tenant_superuser', 'tenant_hr', 'tenant_admin', 'tenant_supervisor'];
  };

  /* ========== FORM ========== */
  const openAddDialog = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '', firstName: '', lastName: '', role: 'tenant_admin', managedSiteIds: [], managedZoneIds: [], shiftId: '', createAsEmployee: true, managerId: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setFormData({
      email: u.email, password: '', firstName: u.firstName, lastName: u.lastName, role: u.role,
      managedSiteIds: u.managedSiteIds || [], managedZoneIds: u.managedZoneIds || [],
      shiftId: u.shiftId || '', createAsEmployee: false, managerId: ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName) { toast.error(fr ? 'Champs requis manquants' : 'Missing required fields'); return; }
    if (!editingUser && !formData.password) { toast.error(fr ? 'Mot de passe requis' : 'Password required'); return; }
    const tenantId = effectiveTenantId || user?.tenantId;
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          email: formData.email, firstName: formData.firstName, lastName: formData.lastName,
          role: formData.role, managedSiteIds: formData.managedSiteIds, managedZoneIds: formData.managedZoneIds,
          shiftId: formData.shiftId || null
        });
        toast.success(fr ? 'Utilisateur modifié' : 'User updated');
      } else {
        const res = await axios.post(`${API}/users`, {
          email: formData.email, password: formData.password, firstName: formData.firstName, lastName: formData.lastName,
          role: formData.role, managedSiteIds: formData.managedSiteIds, managedZoneIds: formData.managedZoneIds,
          shiftId: formData.shiftId || null, tenantId, createAsEmployee: formData.createAsEmployee,
          managerId: formData.managerId && formData.managerId !== 'none' ? formData.managerId : null
        });
        if (res.data.employeeCode) toast.success(fr ? `Utilisateur et employé créés (Code: ${res.data.employeeCode})` : `User and employee created (Code: ${res.data.employeeCode})`);
        else toast.success(fr ? 'Utilisateur créé' : 'User created');
      }
      setDialogOpen(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || (fr ? 'Erreur' : 'Error')); }
  };

  /* ========== DEACTIVATE/ACTIVATE ========== */
  const handleToggleActive = async () => {
    if (!targetUser) return;
    try {
      await axios.put(`${API}/users/${targetUser.id}`, { active: targetUser.active === false });
      toast.success(targetUser.active !== false ? (fr ? 'Utilisateur désactivé' : 'User deactivated') : (fr ? 'Utilisateur réactivé' : 'User reactivated'));
      setDeactivateDialogOpen(false); setTargetUser(null); fetchData();
    } catch (error) { toast.error(fr ? 'Erreur' : 'Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Gestion utilisateurs' : 'User Management'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Accès, rôles et permissions' : 'Access, roles and permissions'}</p>
        </div>
        {canManageUsers && (
          <Button size="sm" className="gap-1.5 h-8" onClick={openAddDialog} data-testid="add-user-btn">
            <Plus size={14} /> {fr ? 'Nouvel utilisateur' : 'New User'}
          </Button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="user-kpis">
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/20">
          <Users size={18} className="text-blue-600 dark:text-blue-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.total}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-green-50/50 dark:bg-green-950/20">
          <UserCheck size={18} className="text-green-600 dark:text-green-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.active}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Actifs' : 'Active'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/20">
          <UserX size={18} className="text-slate-500 dark:text-slate-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.inactive}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Inactifs' : 'Inactive'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-purple-50/50 dark:bg-purple-950/20">
          <Shield size={18} className="text-purple-600 dark:text-purple-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.admins}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Admins' : 'Admins'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-amber-50/50 dark:bg-amber-950/20">
          <Eye size={18} className="text-amber-600 dark:text-amber-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.supervisors}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Superviseurs' : 'Supervisors'}</p></div>
        </div>
      </div>

      {/* Filters */}
      <Card className="dark:border-slate-700" data-testid="user-filters">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={fr ? 'Rechercher nom, email...' : 'Search name, email...'} className="pl-8 h-8 text-sm" data-testid="search-users" />
            </div>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-active"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Tous' : 'All'}</SelectItem>
                <SelectItem value="active">{fr ? 'Actifs' : 'Active'}</SelectItem>
                <SelectItem value="inactive">{fr ? 'Inactifs' : 'Inactive'}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Tous rôles' : 'All roles'}</SelectItem>
                {Object.entries(ROLE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label[language]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="dark:border-slate-700" data-testid="users-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                <TableHead className="text-xs font-bold py-2">{fr ? 'Utilisateur' : 'User'}</TableHead>
                <TableHead className="text-xs font-bold py-2">{fr ? 'Rôle' : 'Role'}</TableHead>
                <TableHead className="text-xs font-bold py-2">{fr ? 'Accès zones/quarts' : 'Zone/Shift Access'}</TableHead>
                <TableHead className="text-xs font-bold py-2">{fr ? 'Statut' : 'Status'}</TableHead>
                <TableHead className="text-xs font-bold py-2 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">{fr ? 'Aucun utilisateur trouvé' : 'No users found'}</TableCell></TableRow>
              ) : (
                filteredUsers.map((u, idx) => (
                  <TableRow key={u.id}
                    className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 ${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''} ${u.active === false ? 'opacity-60' : ''}`}
                    onClick={() => { setDetailUser(u); setDetailOpen(true); }}
                    data-testid={`user-row-${u.id}`}>
                    <TableCell className="py-2">
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{u.firstName} {u.lastName}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1"><Mail size={10} /> {u.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">{getRoleBadge(u.role)}</TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {u.shiftId && (
                          <Badge variant="outline" className="text-[9px] gap-0.5"><Clock size={9} /> {getShiftName(u.shiftId) || 'Quart'}</Badge>
                        )}
                        {u.managedZoneIds?.map(zId => (
                          <Badge key={zId} variant="outline" className="text-[9px] gap-0.5"><MapPin size={9} /> {getZoneName(zId) || 'Zone'}</Badge>
                        ))}
                        {u.managedSiteIds?.map(sId => (
                          <Badge key={sId} variant="outline" className="text-[9px] gap-0.5"><Layers size={9} /> {getSiteName(sId) || 'Site'}</Badge>
                        ))}
                        {!u.shiftId && !u.managedZoneIds?.length && !u.managedSiteIds?.length && (
                          <span className="text-[10px] text-slate-400 italic">{['tenant_superuser', 'tenant_hr', 'platform_superadmin'].includes(u.role) ? (fr ? 'Accès complet' : 'Full access') : '-'}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      {u.active !== false
                        ? <Badge className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 text-[10px]"><UserCheck size={10} className="mr-0.5" /> {fr ? 'Actif' : 'Active'}</Badge>
                        : <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 text-[10px]"><UserX size={10} className="mr-0.5" /> {fr ? 'Inactif' : 'Inactive'}</Badge>
                      }
                    </TableCell>
                    <TableCell className="py-2 text-right" onClick={e => e.stopPropagation()}>
                      {canManageUsers && u.id !== user?.id && (
                        <div className="flex gap-0.5 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditDialog(u)} data-testid={`edit-user-${u.id}`}><Edit size={14} className="text-slate-500" /></Button>
                          {u.active !== false ? (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                              onClick={() => { setTargetUser(u); setDeactivateDialogOpen(true); }}
                              data-testid={`deactivate-user-${u.id}`}><UserX size={14} /></Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20"
                              onClick={() => { setTargetUser(u); setDeactivateDialogOpen(true); }}
                              data-testid={`activate-user-${u.id}`}><UserCheck size={14} /></Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ========== CREATE/EDIT DIALOG ========== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield size={18} /> {editingUser ? (fr ? 'Modifier utilisateur' : 'Edit User') : (fr ? 'Nouvel utilisateur' : 'New User')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-xs">{fr ? 'Prénom' : 'First Name'} *</Label><Input value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} className="h-8 text-sm" data-testid="user-firstname" /></div>
              <div className="space-y-2"><Label className="text-xs">{fr ? 'Nom' : 'Last Name'} *</Label><Input value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} className="h-8 text-sm" data-testid="user-lastname" /></div>
            </div>
            <div className="space-y-2"><Label className="text-xs">Email *</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="h-8 text-sm" data-testid="user-email" /></div>
            {!editingUser && (
              <div className="space-y-2">
                <Label className="text-xs">{fr ? 'Mot de passe' : 'Password'} *</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="h-8 text-sm pr-10" data-testid="user-password" />
                  <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-0 h-8 w-8 p-0" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">{fr ? 'Rôle' : 'Role'} *</Label>
              <Select value={formData.role} onValueChange={v => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="h-8 text-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {getAllowedRoles().map(role => (
                    <SelectItem key={role} value={role}>
                      <span className="font-medium">{ROLE_CONFIG[role].label[language]}</span>
                      <span className="text-[10px] text-slate-500 ml-1">— {ROLE_CONFIG[role].desc[language]}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Supervisor-specific fields */}
            {formData.role === 'tenant_supervisor' && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">{fr ? 'Quart supervisé' : 'Supervised Shift'}</Label>
                  <Select value={formData.shiftId || 'none'} onValueChange={v => setFormData({ ...formData, shiftId: v === 'none' ? '' : v })}>
                    <SelectTrigger className="h-8 text-sm" data-testid="user-shift"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{fr ? 'Aucun' : 'None'}</SelectItem>
                      {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.startTime}-{s.endTime})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{fr ? 'Zones supervisées' : 'Supervised Zones'}</Label>
                  <div className="border dark:border-slate-700 rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                    {zones.length === 0 ? <p className="text-xs text-slate-400">{fr ? 'Aucune zone' : 'No zones'}</p> : (
                      zones.map(z => (
                        <label key={z.id} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                          <input type="checkbox" className="rounded" checked={formData.managedZoneIds.includes(z.id)}
                            onChange={e => setFormData({ ...formData, managedZoneIds: e.target.checked ? [...formData.managedZoneIds, z.id] : formData.managedZoneIds.filter(id => id !== z.id) })} />
                          {z.name}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {formData.role === 'tenant_admin' && (
              <div className="space-y-2">
                <Label className="text-xs">{fr ? 'Sites autorisés (vide = tous)' : 'Allowed Sites (empty = all)'}</Label>
                <Select value={formData.managedSiteIds[0] || 'all'} onValueChange={v => setFormData({ ...formData, managedSiteIds: v === 'all' ? [] : [v] })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{fr ? 'Tous' : 'All'}</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Create as employee option */}
            {!editingUser && ['tenant_supervisor', 'tenant_admin'].includes(formData.role) && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Checkbox id="createAsEmployee" checked={formData.createAsEmployee} onCheckedChange={checked => setFormData({ ...formData, createAsEmployee: checked })} />
                <div>
                  <label htmlFor="createAsEmployee" className="text-sm font-medium text-blue-700 dark:text-blue-400 cursor-pointer">{fr ? 'Créer aussi comme employé' : 'Also create as employee'}</label>
                  <p className="text-[10px] text-blue-600 dark:text-blue-500">{fr ? 'Un code employé sera généré' : 'An employee code will be generated'}</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleSave} data-testid="save-user-btn">{editingUser ? (fr ? 'Modifier' : 'Update') : (fr ? 'Créer' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DEACTIVATE/ACTIVATE DIALOG ========== */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              {targetUser?.active !== false ? <UserX size={18} /> : <UserCheck size={18} />}
              {targetUser?.active !== false ? (fr ? 'Désactiver utilisateur' : 'Deactivate User') : (fr ? 'Réactiver utilisateur' : 'Reactivate User')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {targetUser?.active !== false
              ? (fr ? `Désactiver ${targetUser?.firstName} ${targetUser?.lastName} ? L'utilisateur ne pourra plus se connecter. Son historique est conservé.` : `Deactivate ${targetUser?.firstName} ${targetUser?.lastName}? They won't be able to log in. History is preserved.`)
              : (fr ? `Réactiver ${targetUser?.firstName} ${targetUser?.lastName} ?` : `Reactivate ${targetUser?.firstName} ${targetUser?.lastName}?`)}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className={targetUser?.active !== false ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
              onClick={handleToggleActive} data-testid="confirm-toggle-user-btn">
              {targetUser?.active !== false ? (fr ? 'Désactiver' : 'Deactivate') : (fr ? 'Réactiver' : 'Reactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DETAIL SHEET ========== */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" side="right">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><Shield size={18} /> {detailUser?.firstName} {detailUser?.lastName}</SheetTitle>
            <SheetDescription>{detailUser?.email}</SheetDescription>
          </SheetHeader>
          {detailUser && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700">
                  <p className="text-[10px] text-slate-500 uppercase">{fr ? 'Rôle' : 'Role'}</p>
                  {getRoleBadge(detailUser.role)}
                </div>
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border dark:border-slate-700">
                  <p className="text-[10px] text-slate-500 uppercase">{fr ? 'Statut' : 'Status'}</p>
                  {detailUser.active !== false
                    ? <Badge className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[10px]">{fr ? 'Actif' : 'Active'}</Badge>
                    : <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[10px]">{fr ? 'Inactif' : 'Inactive'}</Badge>}
                </div>
              </div>

              {/* Access Details */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">{fr ? 'Accès et permissions' : 'Access & Permissions'}</p>

                {detailUser.shiftId && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                    <Clock size={14} className="text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-blue-700 dark:text-blue-400">{fr ? 'Quart:' : 'Shift:'} {getShiftName(detailUser.shiftId) || detailUser.shiftId}</span>
                  </div>
                )}

                {detailUser.managedZoneIds?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-500">{fr ? 'Zones supervisées:' : 'Supervised zones:'}</p>
                    {detailUser.managedZoneIds.map(zId => (
                      <div key={zId} className="flex items-center gap-2 p-1.5 rounded bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                        <MapPin size={12} className="text-green-600 dark:text-green-400" />
                        <span className="text-xs text-green-700 dark:text-green-400">{getZoneName(zId) || zId}</span>
                      </div>
                    ))}
                  </div>
                )}

                {detailUser.managedSiteIds?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-slate-500">{fr ? 'Sites autorisés:' : 'Allowed sites:'}</p>
                    {detailUser.managedSiteIds.map(sId => (
                      <div key={sId} className="flex items-center gap-2 p-1.5 rounded bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                        <Layers size={12} className="text-blue-600 dark:text-blue-400" />
                        <span className="text-xs text-blue-700 dark:text-blue-400">{getSiteName(sId) || sId}</span>
                      </div>
                    ))}
                  </div>
                )}

                {['tenant_superuser', 'tenant_hr', 'platform_superadmin'].includes(detailUser.role) && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800">
                    <Lock size={14} className="text-purple-600 dark:text-purple-400" />
                    <span className="text-sm text-purple-700 dark:text-purple-400">{fr ? 'Accès complet' : 'Full access'}</span>
                  </div>
                )}
              </div>

              {/* Module Access */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">{fr ? 'Modules autorisés' : 'Authorized Modules'}</p>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { key: 'dashboard', label: 'Dashboard', all: true },
                    { key: 'reports', label: fr ? 'Rapports' : 'Reports', roles: ['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin', 'tenant_supervisor'] },
                    { key: 'employees', label: fr ? 'Employés' : 'Employees', roles: ['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin'] },
                    { key: 'scheduling', label: fr ? 'Planification' : 'Scheduling', roles: ['platform_superadmin', 'tenant_superuser', 'tenant_admin', 'admin'] },
                    { key: 'payroll', label: fr ? 'Paie' : 'Payroll', roles: ['platform_superadmin', 'tenant_superuser', 'tenant_hr'] },
                    { key: 'analytics', label: fr ? 'Analytique' : 'Analytics', roles: ['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin'] },
                    { key: 'users', label: fr ? 'Utilisateurs' : 'Users', roles: ['platform_superadmin', 'tenant_superuser'] },
                    { key: 'settings', label: fr ? 'Paramètres' : 'Settings', roles: ['platform_superadmin', 'tenant_superuser', 'tenant_admin', 'admin'] },
                  ].map(mod => {
                    const hasAccess = mod.all || mod.roles?.includes(detailUser.role);
                    return (
                      <div key={mod.key} className={`flex items-center gap-1.5 p-1.5 rounded text-xs ${hasAccess ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/20' : 'text-slate-400 dark:text-slate-600 bg-slate-50 dark:bg-slate-800/20'}`}>
                        {hasAccess ? <UserCheck size={10} /> : <Lock size={10} />}
                        {mod.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
